/**
 * Majors Data for Career Exploration
 * Comprehensive data for all majors from CAT assessment
 */

export interface MajorDetail {
  code: string;
  name: string;
  category: string;
  examBlocks: string;
  icon: string;
  salary: string;
  riasec: string;
  employmentRate: string;
  overview: string;
  softSkills: string[];
  subjects: string[];
  traits: string[];
  jobs: { title: string; desc: string; icon: string; hot?: boolean }[];
  universities: { name: string; abbr: string; region: string; note: string }[];
  relatedCareers: { name: string; category: string; icon: string; color: string }[];
  hasFullDetails?: boolean;
}

export const MAJOR_CATEGORIES = [
  { id: 'R', name: 'Kỹ thuật', hollandCode: 'R', color: 'orange' },
  { id: 'I', name: 'Nghiên cứu', hollandCode: 'I', color: 'blue' },
  { id: 'A', name: 'Nghệ thuật', hollandCode: 'A', color: 'purple' },
  { id: 'S', name: 'Xã hội', hollandCode: 'S', color: 'green' },
  { id: 'E', name: 'Quản lý', hollandCode: 'E', color: 'red' },
  { id: 'C', name: 'Nghiệp vụ', hollandCode: 'C', color: 'gray' },
] as const;

// Helper function to generate slug from major name
export const generateSlug = (name: string): string => {
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
};

// Comprehensive majors data
export const MAJORS_DATA: Record<string, MajorDetail> = {
  // ========== KỸ THUẬT (R) ==========
  'cong-nghe-thong-tin': {
    code: '7480201',
    name: 'Công nghệ thông tin',
    category: 'Kỹ thuật & Công nghệ',
    examBlocks: 'A00, A01, D01',
    icon: 'terminal',
    salary: '15 - 30 triệu VNĐ',
    riasec: 'I (Nghiên cứu), R (Kỹ thuật)',
    employmentRate: '96% sau 1 năm',
    hasFullDetails: true,
    overview:
      'Công nghệ thông tin (IT) là ngành sử dụng máy tính và phần mềm máy tính để chuyển đổi, lưu trữ, bảo vệ, xử lý, truyền tải và thu thập thông tin. Đây là nền tảng của kỷ nguyên số, đóng vai trò then chốt trong sự phát triển kinh tế và xã hội hiện đại.\n\nNgười làm việc trong ngành này thường xuyên tiếp xúc với các ngôn ngữ lập trình, hệ quản trị cơ sở dữ liệu và các giải pháp mạng. Môi trường làm việc năng động, đòi hỏi sự sáng tạo liên tục và khả năng tự học cao để bắt kịp xu hướng công nghệ thay đổi từng ngày.',
    subjects: ['Toán học (Logic)', 'Tin học', 'Tiếng Anh', 'Vật lí'],
    softSkills: [
      'Tư duy logic và giải quyết vấn đề phức tạp',
      'Khả năng tự học và thích ứng nhanh',
      'Làm việc nhóm (Teamwork) và giao tiếp',
    ],
    traits: [
      'Thích mày mò, tìm hiểu về máy tính, điện thoại và các ứng dụng mới.',
      'Có tính kiên trì, tỉ mỉ và chịu được áp lực cao.',
      'Giỏi các môn tự nhiên và có trí nhớ tốt.',
    ],
    jobs: [
      {
        title: 'Lập trình viên (Software Developer)',
        desc: 'Thiết kế, xây dựng và bảo trì các ứng dụng phần mềm cho máy tính hoặc thiết bị di động.',
        icon: 'code',
        hot: true,
      },
      {
        title: 'Chuyên viên An ninh mạng',
        desc: 'Bảo vệ hệ thống mạng và dữ liệu của tổ chức khỏi các cuộc tấn công mạng và rủi ro bảo mật.',
        icon: 'shield',
      },
      {
        title: 'Chuyên viên Phân tích Dữ liệu',
        desc: 'Thu thập, xử lý và phân tích dữ liệu để giúp doanh nghiệp đưa ra các quyết định chiến lược.',
        icon: 'analytics',
      },
    ],
    universities: [
      { name: 'ĐH Bách Khoa Hà Nội', abbr: 'HUST', region: 'Miền Bắc', note: 'Điểm chuẩn: Cao' },
      {
        name: 'ĐH Công Nghệ - ĐHQGHN',
        abbr: 'VNU-UET',
        region: 'Miền Bắc',
        note: 'Điểm chuẩn: Cao',
      },
      { name: 'Đại học FPT', abbr: 'FPT', region: 'Toàn quốc', note: 'Xét tuyển riêng' },
      { name: 'ĐH CNTT - ĐHQG TP.HCM', abbr: 'UIT', region: 'Miền Nam', note: 'Điểm chuẩn: Cao' },
    ],
    relatedCareers: [
      {
        name: 'Kỹ thuật máy tính',
        category: 'Phần cứng & Hệ thống',
        icon: 'memory',
        color: 'indigo',
      },
      {
        name: 'Hệ thống thông tin',
        category: 'Quản lý & Vận hành',
        icon: 'database',
        color: 'teal',
      },
      { name: 'Trí tuệ nhân tạo', category: 'Khoa học máy tính', icon: 'smart_toy', color: 'pink' },
    ],
  },
  'co-khi-che-tao': {
    code: '7520103',
    name: 'Cơ khí Chế tạo',
    category: 'Kỹ thuật & Công nghệ',
    examBlocks: 'A00, A01',
    icon: 'settings',
    salary: '12 - 25 triệu VNĐ',
    riasec: 'R (Kỹ thuật)',
    employmentRate: '92% sau 1 năm',
    hasFullDetails: false,
    overview:
      'Ngành đào tạo kỹ sư có khả năng thiết kế, chế tạo, vận hành và bảo trì các hệ thống cơ khí trong sản xuất công nghiệp.',
    subjects: ['Toán học', 'Vật lí', 'Vẽ kỹ thuật'],
    softSkills: ['Tư duy logic', 'Khả năng làm việc nhóm', 'Kỹ năng giải quyết vấn đề'],
    traits: ['Thích làm việc với máy móc', 'Tỉ mỉ và cẩn thận', 'Có khả năng tư duy không gian'],
    jobs: [{ title: 'Kỹ sư cơ khí', desc: 'Thiết kế và phát triển sản phẩm cơ khí', icon: 'cog' }],
    universities: [
      { name: 'ĐH Bách Khoa Hà Nội', abbr: 'HUST', region: 'Miền Bắc', note: 'Top 1' },
    ],
    relatedCareers: [{ name: 'Cơ điện tử', category: 'Kỹ thuật', icon: 'chip', color: 'indigo' }],
  },

  // ========== NGHIÊN CỨU (I) ==========
  'y-hoc': {
    code: '7720101',
    name: 'Y học',
    category: 'Khoa học Sức khỏe',
    examBlocks: 'A00, B00, D07',
    icon: 'heart_pulse',
    salary: '15 - 40 triệu VNĐ',
    riasec: 'I (Nghiên cứu), S (Xã hội)',
    employmentRate: '98% sau 1 năm',
    hasFullDetails: false,
    overview:
      'Y học đào tạo bác sĩ có kiến thức sâu rộng về cơ thể con người, bệnh lý và phương pháp điều trị, chăm sóc sức khỏe cộng đồng.',
    subjects: ['Sinh học', 'Hóa học', 'Vật lí'],
    softSkills: ['Kỹ năng giao tiếp', 'Đồng cảm', 'Ra quyết định nhanh'],
    traits: ['Có trách nhiệm cao', 'Kiên nhẫn', 'Thích giúp đỡ người khác'],
    jobs: [
      {
        title: 'Bác sĩ đa khoa',
        desc: 'Chẩn đoán và điều trị bệnh',
        icon: 'stethoscope',
        hot: true,
      },
    ],
    universities: [{ name: 'ĐH Y Hà Nội', abbr: 'HMU', region: 'Miền Bắc', note: 'Top đầu' }],
    relatedCareers: [{ name: 'Dược học', category: 'Sức khỏe', icon: 'pill', color: 'green' }],
  },

  // ========== NGHỆ THUẬT (A) ==========
  'thiet-ke-do-hoa': {
    code: '7210403',
    name: 'Thiết kế đồ họa',
    category: 'Nghệ thuật & Truyền thông',
    examBlocks: 'D10, D14, V00',
    icon: 'palette',
    salary: '10 - 25 triệu VNĐ',
    riasec: 'A (Nghệ thuật)',
    employmentRate: '85% sau 1 năm',
    hasFullDetails: false,
    overview:
      'Đào tạo kỹ năng sáng tạo thiết kế hình ảnh, bố cục và truyền tải thông điệp qua các phương tiện truyền thông đa phương tiện.',
    subjects: ['Mỹ thuật', 'Tin học', 'Tiếng Anh'],
    softSkills: ['Sáng tạo', 'Tư duy thẩm mỹ', 'Giao tiếp với khách hàng'],
    traits: ['Yêu thích nghệ thuật', 'Có óc thẩm mỹ', 'Theo kịp xu hướng'],
    jobs: [
      {
        title: 'Nhà thiết kế đồ họa',
        desc: 'Thiết kế hình ảnh cho quảng cáo, web, app',
        icon: 'design',
        hot: true,
      },
    ],
    universities: [
      { name: 'ĐH Mỹ thuật Công nghiệp', abbr: 'MHCI', region: 'Miền Bắc', note: 'Chuyên ngành' },
    ],
    relatedCareers: [
      { name: 'Thiết kế thời trang', category: 'Nghệ thuật', icon: 'shirt', color: 'pink' },
    ],
  },

  // ========== XÃ HỘI (S) ==========
  'su-pham': {
    code: '7140101',
    name: 'Sư phạm',
    category: 'Khoa học Giáo dục',
    examBlocks: 'C00, D01, A00',
    icon: 'school',
    salary: '8 - 18 triệu VNĐ',
    riasec: 'S (Xã hội)',
    employmentRate: '90% sau 1 năm',
    hasFullDetails: false,
    overview:
      'Đào tạo giáo viên với phương pháp sư phạm hiện đại, kiến thức chuyên môn và kỹ năng truyền đạt tri thức cho thế hệ trẻ.',
    subjects: ['Tâm lý học', 'Giáo dục học', 'Chuyên môn'],
    softSkills: ['Giao tiếp', 'Kiên nhẫn', 'Tổ chức'],
    traits: ['Yêu trẻ', 'Nhẫn nại', 'Trách nhiệm cao'],
    jobs: [{ title: 'Giáo viên', desc: 'Giảng dạy tại các trường phổ thông', icon: 'teacher' }],
    universities: [
      { name: 'ĐH Sư phạm Hà Nội', abbr: 'HNUE', region: 'Miền Bắc', note: 'Top sư phạm' },
    ],
    relatedCareers: [{ name: 'Tâm lý học', category: 'Xã hội', icon: 'psychology', color: 'teal' }],
  },

  // ========== QUẢN LÝ (E) ==========
  'quan-tri-kinh-doanh': {
    code: '7340101',
    name: 'Quản trị kinh doanh',
    category: 'Kinh tế & Quản lý',
    examBlocks: 'A00, A01, D01, D07',
    icon: 'briefcase',
    salary: '12 - 30 triệu VNĐ',
    riasec: 'E (Quản lý)',
    employmentRate: '88% sau 1 năm',
    hasFullDetails: false,
    overview:
      'Đào tạo kỹ năng quản lý, lãnh đạo doanh nghiệp, lập kế hoạch kinh doanh và phát triển chiến lược trong môi trường cạnh tranh.',
    subjects: ['Toán', 'Tiếng Anh', 'Kinh tế'],
    softSkills: ['Lãnh đạo', 'Ra quyết định', 'Thuyết phục'],
    traits: ['Tự tin', 'Quyết đoán', 'Tham vọng'],
    jobs: [
      {
        title: 'Quản lý doanh nghiệp',
        desc: 'Vận hành và phát triển doanh nghiệp',
        icon: 'business',
        hot: true,
      },
    ],
    universities: [
      { name: 'ĐH Ngoại thương', abbr: 'FTU', region: 'Miền Bắc', note: 'Top kinh tế' },
    ],
    relatedCareers: [
      { name: 'Marketing', category: 'Kinh doanh', icon: 'campaign', color: 'orange' },
    ],
  },

  // ========== NGHIỆP VỤ (C) ==========
  'ke-toan': {
    code: '7340301',
    name: 'Kế toán',
    category: 'Kinh tế & Tài chính',
    examBlocks: 'A00, A01, D01, D07',
    icon: 'calculator',
    salary: '10 - 20 triệu VNĐ',
    riasec: 'C (Nghiệp vụ)',
    employmentRate: '94% sau 1 năm',
    hasFullDetails: false,
    overview:
      'Đào tạo chuyên viên kế toán với kiến thức về ghi chép, báo cáo tài chính, thuế và kiểm soát tài chính doanh nghiệp.',
    subjects: ['Toán', 'Tiếng Anh', 'Kế toán'],
    softSkills: ['Tỉ mỉ', 'Chính xác', 'Trung thực'],
    traits: ['Cẩn thận', 'Kiên nhẫn', 'Làm việc theo quy trình'],
    jobs: [
      {
        title: 'Kế toán viên',
        desc: 'Quản lý sổ sách tài chính doanh nghiệp',
        icon: 'account_balance',
      },
    ],
    universities: [
      { name: 'ĐH Kinh tế Quốc dân', abbr: 'NEU', region: 'Miền Bắc', note: 'Top kinh tế' },
    ],
    relatedCareers: [
      {
        name: 'Tài chính ngân hàng',
        category: 'Tài chính',
        icon: 'account_balance',
        color: 'blue',
      },
    ],
  },
};

// Helper to get all majors as array
export const getAllMajors = (): Array<MajorDetail & { slug: string }> => {
  return Object.entries(MAJORS_DATA).map(([slug, data]) => ({
    ...data,
    slug,
  }));
};

import { GROUP_INFO } from './catData';

export const RIASEC_MAP: Record<
  string,
  { code: string; name: string; category: string; icon: string }
> = {
  group1: { code: 'R', name: 'Kỹ thuật', category: 'Kỹ thuật & Công nghệ', icon: 'settings' },
  group2: { code: 'I', name: 'Nghiên cứu', category: 'Khoa học & Nghiên cứu', icon: 'microscope' },
  group3: { code: 'A', name: 'Nghệ thuật', category: 'Nghệ thuật & Sáng tạo', icon: 'palette' },
  group4: { code: 'S', name: 'Xã hội', category: 'Xã hội & Giáo dục', icon: 'users' },
  group5: { code: 'E', name: 'Quản lý', category: 'Kinh tế & Quản lý', icon: 'briefcase' },
  group6: { code: 'C', name: 'Nghiệp vụ', category: 'Tài chính & Nghiệp vụ', icon: 'file-text' },
};

// Helper to get major by slug
export const getMajorBySlug = (slug: string): (MajorDetail & { slug: string }) | null => {
  // 1. Try to find in detailed MAJORS_DATA first
  const data = MAJORS_DATA[slug];
  if (data) return { ...data, slug };

  // 2. If not found, try to find in GROUP_INFO (catData.ts)
  let foundMajorName = '';
  let foundGroupId = '';

  for (const [groupId, info] of Object.entries(GROUP_INFO)) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const group = info as any;

    // Search in thptMajors (flat array)
    if (group.thptMajors) {
      const match = (group.thptMajors as string[]).find(
        (name: string) => generateSlug(name) === slug
      );
      if (match) {
        foundMajorName = match;
        foundGroupId = groupId;
        break;
      }
    }

    // Search in thptMajorGroups (nested structure with .majors array)
    if (group.thptMajorGroups) {
      for (const majorGroup of group.thptMajorGroups) {
        const match = (majorGroup.majors as string[])?.find(
          (name: string) => generateSlug(name) === slug
        );
        if (match) {
          foundMajorName = match;
          foundGroupId = groupId;
          break;
        }
      }
      if (foundMajorName) break;
    }

    // Search in thcsCareerGroups (nested structure with .careers array)
    if (group.thcsCareerGroups) {
      for (const careerGroup of group.thcsCareerGroups) {
        const match = (careerGroup.careers as string[])?.find(
          (name: string) => generateSlug(name) === slug
        );
        if (match) {
          foundMajorName = match;
          foundGroupId = groupId;
          break;
        }
      }
      if (foundMajorName) break;
    }
  }

  if (foundMajorName && foundGroupId) {
    const groupInfo = GROUP_INFO[foundGroupId as keyof typeof GROUP_INFO];
    const riasecInfo = RIASEC_MAP[foundGroupId];

    return {
      slug,
      code: '---', // Placeholder
      name: foundMajorName,
      category: riasecInfo.category,
      examBlocks: 'A00, A01, D01, ...', // Generic
      icon: riasecInfo.icon,
      salary: 'Đang cập nhật',
      riasec: `${riasecInfo.code} (${riasecInfo.name})`,
      employmentRate: 'Cao',
      overview: groupInfo.fullDesc || `Ngành ${foundMajorName} thuộc nhóm ${riasecInfo.name}.`,
      subjects: ['Toán', 'Văn', 'Anh'], // Generic
      softSkills: ['Giao tiếp', 'Làm việc nhóm', 'Tư duy logic'], // Generic
      traits: groupInfo.shortDescPoints || [],
      jobs: [
        {
          title: `Cử nhân ${foundMajorName}`,
          desc: `Làm việc trong lĩnh vực ${detailedName(foundMajorName)}`,
          icon: riasecInfo.icon,
        },
      ],
      universities: [
        {
          name: 'Đang cập nhật',
          abbr: 'N/A',
          region: 'Toàn quốc',
          note: 'Tham khảo quy chế tuyển sinh',
        },
      ],
      relatedCareers: [],
      hasFullDetails: false, // Flag to indicate generated data
    };
  }

  return null;
};

function detailedName(name: string) {
  return name.toLowerCase();
}

// Helper to filter majors by category
export const getMajorsByCategory = (categoryId: string): Array<MajorDetail & { slug: string }> => {
  const categoryMap: Record<string, string[]> = {
    R: ['Kỹ thuật'],
    I: ['Khoa học', 'Nghiên cứu'],
    A: ['Nghệ thuật'],
    S: ['Xã hội', 'Giáo dục', 'Sức khỏe'],
    E: ['Quản lý', 'Kinh tế', 'Kinh doanh'],
    C: ['Nghiệp vụ', 'Tài chính'],
  };

  const keywords = categoryMap[categoryId] || [];
  // Note: This only filters from MAJORS_DATA currently.
  // If we want to filter ALL potential majors, we'd need to generate them all first.
  // For now, let's keep it consistent with previous behavior or expand if needed.
  return getAllMajors().filter(m =>
    keywords.some(k => m.category.includes(k) || m.riasec.includes(categoryId))
  );
};
